import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { EnvironmentConfig } from '@core/config-models';
import { TranslateService } from '@ngx-translate/core';

import {
  brandJsonTranslationNames,
  dutchIsoLanguage,
  jsonTranslationName,
} from './defaults';
import { JsonModule, PartialFileName } from './interfaces';

/**
 * Service to load JSON translation files (both non-brand and brand specific).
 * Mainly used to load feature specific translations.
 */
@Injectable({
  providedIn: 'root',
})
export class LoadJsonTranslationsService {
  constructor(
    @Inject(ENVIRONMENT_CONFIG) private readonly config: EnvironmentConfig,
    private readonly translateService: TranslateService
  ) {}

  /**
   * Load the specified json translation files into the translationService
   *
   * @param namespace the namespace to add these to the translationService
   * @param importFn callback to import a json file using webpack's dynamic import
   */
  async loadTranslationFiles(
    namespace: string,
    importFn: (path: PartialFileName) => Promise<JsonModule>
  ): Promise<void> {
    const translations = await this.importTranslations(
      jsonTranslationName,
      importFn,
      // We ignore errors for brand-specific files because they are optional.
      // If the main translation file is not found we do throw an error because that one is mandatory.
      true
    );
    this.translateService.setTranslation(
      dutchIsoLanguage,
      { [namespace]: translations },
      true
    );

    const brandTranslations = await this.importTranslations(
      brandJsonTranslationNames[this.config.brand],
      importFn
    );
    if (brandTranslations) {
      this.translateService.setTranslation(
        dutchIsoLanguage,
        { [namespace]: brandTranslations },
        true
      );
    }
    // Sometimes keys aren't translated, due to the language files not being loaded in time.
    // This line forcibly loads the language files and waits until it is completed
    await this.translateService.use(dutchIsoLanguage).toPromise();
  }

  private async importTranslations(
    translationName: PartialFileName,
    importFn: (path: PartialFileName) => Promise<JsonModule>,
    logErrorIfNotFound = false
  ) {
    return importFn(translationName)
      .then((module) => module.default)
      .catch((error) => {
        if (logErrorIfNotFound) {
          console.error('failed to load local json file:', {
            translationName,
            error,
          });
        }
      });
  }
}
